/**
 * Сповіщення про нові повідомлення — бабли в правому нижньому куті.
 *
 * Джерела два: чат Twitch (приходить пушем через tmi.js) і гілки comments.app
 * (їх сам віджет назовні не віддає, тому сервер раз на POLL_INTERVAL забирає
 * останні коментарі через /api/chatFeed).
 *
 * Стан спільний на весь застосунок: опитування піднімає плаваючий чат, а
 * показувати бабли може і він, і плеєр у фулскріні.
 */

const STORAGE_KEY = 'site_chat_notifications'
const POLL_INTERVAL = 20000
const BUBBLE_LIFETIME = 60000
const MAX_BUBBLES = 4

const DEFAULT_SETTINGS = {
  enabled: true,
  twitch: true,
  site: true,
  anime: true,
  episode: true
}

// Підпис у баблі — з якого саме чату повідомлення
export const SOURCE_LABELS = {
  twitch: 'Twitch',
  site: 'Чат сайту',
  anime: 'Чат аніме',
  episode: 'Чат серії'
}

export const useChatNotifications = () => {
  const settings = useState('chatNotificationSettings', () => ({
    ...DEFAULT_SETTINGS
  }))
  const bubbles = useState('chatBubbles', () => [])
  // Непрочитані — все, що прилетіло, поки чат не було видно
  const unread = useState('chatUnread', () => 0)

  // Чат «видно», якщо відкрите плаваюче вікно або панель кінотеатру в плеєрі
  const windowOpen = useState('twitchChatOpen', () => false)
  const panelOpen = useState('siteChatPanelOpen', () => false)
  const chatVisible = computed(() => windowOpen.value || panelOpen.value)
  // pageId -> id останнього побаченого коментаря
  const lastSeen = useState('chatFeedLastSeen', () => ({}))

  const { context } = useSiteChat()

  const loadSettings = () => {
    if (!import.meta.client) return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) settings.value = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) }
    } catch (e) {}
  }

  const saveSettings = () => {
    if (!import.meta.client) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value))
    } catch (e) {}
  }

  const toggleSetting = (key) => {
    settings.value = { ...settings.value, [key]: !settings.value[key] }
    saveSettings()
  }

  const hideBubble = (key) => {
    bubbles.value = bubbles.value.filter((b) => b.key !== key)
  }

  const pushBubble = (bubble) => {
    const key = `${bubble.source}-${bubble.id}-${Date.now()}-${Math.random()}`
    bubbles.value = [...bubbles.value, { ...bubble, key }].slice(-MAX_BUBBLES)
    if (!chatVisible.value) unread.value++
    setTimeout(() => hideBubble(key), BUBBLE_LIFETIME)
  }

  const resetUnread = () => {
    unread.value = 0
  }

  const clearBubbles = (source = null) => {
    bubbles.value = source
      ? bubbles.value.filter((b) => b.source !== source)
      : []
  }

  // --- Опитування гілок comments.app ---

  // Які гілки стежити зараз: загальна завжди, аніме і серія — коли відкрита
  // сторінка перегляду. Вимкнені в налаштуваннях не тягнемо взагалі.
  const watchedThreads = computed(() => {
    const list = []
    if (settings.value.site) list.push({ source: 'site', pageId: 'site-chat' })

    const ctx = context.value
    if (ctx?.slug) {
      // Куди саме написали — щоб бабл міг це показати і привести назад
      const target = {
        slug: ctx.slug,
        animeTitle: ctx.title || ctx.slug
      }

      if (settings.value.anime) {
        list.push({ source: 'anime', pageId: ctx.slug, target })
      }
      if (
        settings.value.episode &&
        ctx.episodeIndex !== null &&
        ctx.episodeIndex !== undefined
      ) {
        list.push({
          source: 'episode',
          pageId: `${ctx.slug}-ep${ctx.episodeIndex + 1}`,
          target: {
            ...target,
            episodeIndex: ctx.episodeIndex,
            episodeTitle: ctx.episodeTitle || `Серія ${ctx.episodeIndex + 1}`
          }
        })
      }
    }
    return list
  })

  let timer = null
  let inFlight = false

  const poll = async () => {
    if (!import.meta.client || inFlight) return
    if (!settings.value.enabled) return
    // У фоновій вкладці бабли все одно нікому показувати
    if (document.hidden) return

    const threads = watchedThreads.value
    if (!threads.length) return

    inFlight = true
    try {
      const { threads: data } = await $fetch('/api/chatFeed', {
        query: { ids: threads.map((t) => t.pageId).join(',') }
      })

      for (const { source, pageId, target } of threads) {
        const comments = data?.[pageId]?.comments || []
        if (!comments.length) continue

        const newest = comments[comments.length - 1].id
        const seen = lastSeen.value[pageId]

        // Перший обхід гілки — тільки запамʼятовуємо, звідки рахувати, інакше
        // на людину висипався б увесь хвіст старих коментарів
        if (seen === undefined) {
          lastSeen.value = { ...lastSeen.value, [pageId]: newest }
          continue
        }

        comments
          .filter((c) => c.id > seen)
          .forEach((c) =>
            pushBubble({
              source,
              id: c.id,
              author: c.author,
              text: c.text,
              avatar: c.avatar,
              target
            })
          )

        if (newest > seen) {
          lastSeen.value = { ...lastSeen.value, [pageId]: newest }
        }
      }
    } catch (e) {
      // Мовчки: чат — не критичний функціонал, а помилка мережі тут звична
    } finally {
      inFlight = false
    }
  }

  const startFeed = () => {
    if (!import.meta.client || timer) return
    poll()
    timer = setInterval(poll, POLL_INTERVAL)
    document.addEventListener('visibilitychange', poll)
    // Відкрили чат — усе побачене. Вішаємо тут, бо startFeed кличе рівно один
    // компонент (плаваючий віджет), а сам композабл смикають кілька
    watch(chatVisible, (val) => {
      if (val) resetUnread()
    })
  }

  const stopFeed = () => {
    if (timer) clearInterval(timer)
    timer = null
    if (import.meta.client) {
      document.removeEventListener('visibilitychange', poll)
    }
  }

  return {
    settings,
    bubbles,
    unread,
    chatVisible,
    resetUnread,
    watchedThreads,
    loadSettings,
    saveSettings,
    toggleSetting,
    pushBubble,
    hideBubble,
    clearBubbles,
    startFeed,
    stopFeed,
    poll
  }
}
